import { clsx } from 'clsx';
import type { BodyRegionDef, BodyRegionSelection, BodySide } from '../api/referralsApi';
import styles from './chips.module.css';

const SIDES: Array<{ value: BodySide; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'bilateral', label: 'Both' },
];

interface BodyRegionPickerProps {
  regions: BodyRegionDef[];
  selected: BodyRegionSelection[];
  onChange?: (next: BodyRegionSelection[]) => void;
  readOnly?: boolean;
}

/**
 * Medical slips: pick the affected region, then its side when the region has one. Sits where the
 * dental slip shows the tooth chart.
 */
export function BodyRegionPicker({ regions, selected, onChange, readOnly = false }: BodyRegionPickerProps) {
  const groups = [...new Set(regions.map((r) => r.group))];
  const isOn = (key: string) => selected.some((s) => s.region === key);
  const byKey = new Map(regions.map((r) => [r.key, r]));

  const toggle = (key: string) => {
    if (readOnly || !onChange) return;
    onChange(isOn(key) ? selected.filter((s) => s.region !== key) : [...selected, { region: key, side: null }]);
  };
  const setSide = (key: string, side: BodySide) => {
    if (readOnly || !onChange) return;
    onChange(selected.map((s) => (s.region === key ? { ...s, side: s.side === side ? null : side } : s)));
  };

  return (
    <div className={styles.group}>
      {!readOnly &&
        groups.map((group) => (
          <div key={group} className={styles.group}>
            <span className={styles.groupLabel}>{group}</span>
            <div className={styles.row}>
              {regions
                .filter((r) => r.group === group)
                .map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    className={clsx(styles.chip, styles.chipSmall, isOn(r.key) && styles.chipOn)}
                    onClick={() => toggle(r.key)}
                    aria-pressed={isOn(r.key)}
                  >
                    {r.label}
                  </button>
                ))}
            </div>
          </div>
        ))}
      {selected.length > 0 && (
        <div className={styles.group}>
          {!readOnly && <span className={styles.groupLabel}>Selected</span>}
          {selected.map((s) => {
            const def = byKey.get(s.region);
            if (!def) return null;
            return (
              <div key={s.region} className={styles.sideRow}>
                <span className={styles.sideLabel}>{def.label}</span>
                {def.sided &&
                  (readOnly ? (
                    <span className={clsx(styles.chip, styles.chipSmall, styles.chipOn)}>
                      {s.side ? SIDES.find((x) => x.value === s.side)?.label ?? s.side : 'Side not noted'}
                    </span>
                  ) : (
                    SIDES.map((side) => (
                      <button
                        key={side.value}
                        type="button"
                        className={clsx(styles.chip, styles.chipSmall, s.side === side.value && styles.chipOn)}
                        onClick={() => setSide(s.region, side.value)}
                        aria-pressed={s.side === side.value}
                      >
                        {side.label}
                      </button>
                    ))
                  ))}
                {!readOnly && (
                  <button type="button" className={styles.remove} onClick={() => toggle(s.region)}>
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {readOnly && selected.length === 0 && <p className={styles.empty}>No affected area noted.</p>}
    </div>
  );
}
